import { Predicate } from '@app/types';
import * as R from 'ramda';

import { isNotEmpty } from './type';

interface ContainsAny {
  <T>(xs: T[], ys: T[]): boolean;
  <T>(xs: T[]): (ys: T[]) => boolean;
}

/**
 * Returns true if both lists have elements in common.
 *
 * @param list1 list
 * @param list2 list
 * @return true if both lists have elements in common.
 */
export const containsAny: ContainsAny = R.curry((list1, list2) =>
  isNotEmpty(R.intersection(list1, list2))
);

interface AnyOf {
  <T> (xs: T[], predicate: Predicate<T>): boolean;
  <T> (xs: T[]): (predicate: Predicate<T>) => boolean;
}

/**
 * Returns true if at least one of the elements of the list match the predicate, false otherwise.
 *
 * @param T
 * @param {Array<T>} list
 * @param {Predicate<T>} predicate
 * @return {boolean} true if at least one of the elements of the list match the predicate
 */
export const anyOf: AnyOf = R.flip(R.any);

interface ArrayLengthGreaterThan {
  (n: number, xs: any[]): boolean;
  (n: number): (xs: any[]) => boolean;
}

export const arrayLengthGreaterThan: ArrayLengthGreaterThan = R.curry((length: number, list: any[]) =>
  R.compose(R.lt(length), R.length)(list)
);


export const zipAllWith = R.curry((fn, lists) => {
  const minLength = Math.min(...R.map(R.length, lists));
  const zipIndices = R.range(0, minLength);

  return R.map((i: number) =>
    fn(...R.map(R.nth(i), lists)),
  zipIndices
  );
});

type ZipAll = <T> (xs: T[][]) => T[][];
export const zipAll: ZipAll = zipAllWith((...args) => args);

interface MinBy {
  <T> (fn: (T) => number, xs: T[]): T;
  <T> (fn: (T) => number): (xs: T[]) => T;
}

/**
 * Returns the item of the list that produces the smallest result when passed to the provided function.
 *
 * @param fn a function that takes an item as a parameter and returns a comparable value
 * @param anArray array of items
 * @return the item of the list that produces the smallest result when passed to the provided function.
 */
export const minBy: MinBy = R.curry((fn, anArray) =>
  R.reduce(R.minBy(fn), R.head(anArray), anArray)
);
